import type { ComponentPropsWithoutRef } from "react"
import { forwardRef } from "react"
import Balancer from "react-wrap-balancer"

import { cn } from "@productlane/lib/src/browser"
import { Button, ProductlaneIcon } from "@productlane/ui"

import type { SlideBaseProps } from "./slide-wrapper"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"

export const ReadySlide = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<"div"> & SlideBaseProps
>(({ nextSlide, className, ...props }, ref) => {
	const { session } = useSession()
	const { mutate: mutateWorkspace, isLoading: workspaceLoading } =
		api.workspace.update.useMutation({
			onSuccess: () => {
				if (session) {
					try {
						analytics.track(`user-onboarding-completed`, {
							userId: session.userId,
						})
						analytics.track(`setup-workspace-completed`, {
							userId: session.userId,
							workspaceId: session.workspaceId,
						})
					} catch {
						// Never let analytics failures block the user from entering the app.
					}
				}
				nextSlide()
			},
			onError: (error) => {
				if (error.data?.workspaceDeleted) return
				toast.error({
					title: "Couldn't finish setup",
					description: error.message || "Please try again.",
				})
			},
		})
	return (
		<div
			ref={ref}
			className={cn("flex flex-col items-center pt-1.5", className)}
			{...props}
		>
			<div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#191A1C] ring-1 ring-white/10">
				<ProductlaneIcon fill="white" className="h-10" />
			</div>
			<div className="mb-1 mt-3 text-center text-[2.2rem] font-semibold leading-[1.15] tracking-tight">
				Your 7 day trial has started
			</div>
			<Balancer className="mb-8 mt-1 text-center text-secondary">
				You can now set up your customer support system around Linear.
			</Balancer>

			<Button
				size="xl"
				className="mt-4 w-full max-w-xxs"
				onClick={() => {
					mutateWorkspace({ hasBeenSetup: true })
				}}
				disabled={workspaceLoading}
			>
				Open Productlane
			</Button>
		</div>
	)
})
